/**
 * The client for THIS app's server. It talks to `/api/*` and nothing else.
 *
 * There is no credential here and there must never be one. `ERA_STREAM_API_KEY`
 * identifies the calling service, not a user; a browser holding it could
 * create rooms and read every session in the system. That is the entire
 * reason this app has a server half.
 *
 * Same-origin relative URLs, in development and in production alike: Vite
 * proxies `/api` to the BFF, and in production the BFF serves this bundle
 * itself. One shape, exercised in both modes.
 */

import type { ApiErrorBody } from '@era/shared'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly retryable: boolean

  constructor(status: number, code: string, detail: string, retryable: boolean, cause?: unknown) {
    super(detail, cause === undefined ? undefined : { cause })
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryable = retryable
  }

  /**
   * The upstream had no node with capacity. Retrying is the correct and only
   * response — never fall back to a cached URL or pick a node client-side.
   */
  get noCapacity(): boolean {
    return this.status === 503
  }

  get notImplemented(): boolean {
    return this.status === 501
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  signal?: AbortSignal
  /** Creator-only room capability. Sent only in the redacted auth header. */
  controlToken?: string
  /** Another explicitly scoped bearer, such as one-time same-identity resume. */
  bearerToken?: string
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  const bearer = options.bearerToken ?? options.controlToken
  if (bearer) headers.Authorization = `Bearer ${bearer}`

  let response: Response
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    })
  } catch (cause) {
    // fetch rejects identically for a dead server, a DNS failure and a blocked
    // request. In this app there is only one plausible cause worth naming.
    throw new ApiError(0, 'unreachable', 'the era-meet server is not responding', true, cause)
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const parsed: unknown = text ? safeParse(text) : null

  if (!response.ok) {
    const body = parsed as Partial<ApiErrorBody> | null
    throw new ApiError(
      response.status,
      body?.error ?? `http_${response.status}`,
      body?.detail ?? response.statusText,
      body?.retryable ?? response.status >= 500,
    )
  }

  return parsed as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * The URL of a streamable artifact.
 *
 * A plain URL, given straight to `<audio src>`, deliberately — NOT a fetch
 * into a blob. The BFF forwards HTTP Range to the control plane's proxy and
 * passes the 206 back, which is what lets the element seek without downloading
 * the whole recording first. Wrapping it in a blob would silently throw that
 * away and look like a slow player.
 */
export function artifactUrl(sessionId: string, artifactId: string): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`
}
